import React, { useEffect, useState } from "react";
import { sendRequest } from "../ApiRequest";
import { BASE_URL } from "../URL/constants";
import getCurrentUser from "../components/common/GetCurrentUser";
import { useNavigate } from "react-router-dom";
import MessageModal from "./Common/MessageModal";
import useMessageModal from "../hooks/useMessageModal";
import "./WishlistPage.css";
import Login from "./buttons/Login";

const WishlistPage = () => {
  const { currentUser } = getCurrentUser();
  const [wishlistItems, setWishlistItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const token = currentUser?.data?.token;
  const navigate = useNavigate();
  const [loginModalOpen, setLoginModalOpen] = useState(false);
  const [customerData, setCustomerData] = useState(null);
  const { modalState, showSuccess, showError, showWarning, closeModal } = useMessageModal();


  useEffect(() => {
    const fetchWishlist = async () => {
      try {
        const response = await sendRequest(
          "GET",
          `${BASE_URL}/v1/public_site/wishlists`,
          null,
          { Authorization: token }
        );
        setWishlistItems(response.data || []);
      } catch (error) {
        console.error("Error fetching wishlist:", error);
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      fetchWishlist();
    } else {
      setLoading(false);
    }
  }, [token]);

  const handleRemove = async (itemId) => {
    try {
      await sendRequest(
        "DELETE",
        `${BASE_URL}/v1/public_site/wishlists/${itemId}`,
        null,
        { Authorization: token }
      );
      setWishlistItems((prev) =>
        prev.filter((item) => item.item_id !== itemId)
      );
      showSuccess("Item removed from wishlist successfully!");
    } catch (error) {
      console.error("Error removing item from wishlist:", error);
      showError("Failed to remove item from wishlist.");
    }
  };

  const handleMoveToCart = async (item) => {
    try {
      const cartData = {
        customer_id: currentUser.data.customer.id,
        item_id: item.item_id,
        quantity: 1,
      };

      await sendRequest(
        "POST",
        `${BASE_URL}/v1/public_site/customers/${currentUser.data.customer.id}/cart/cart_items`,
        cartData,
        { Authorization: token }
      );

      // Remove from wishlist after adding to cart
      await handleRemove(item.item_id);
      showSuccess("Item moved to cart successfully!");
    } catch (error) {
      console.error("Error moving item to cart:", error);
      showError("Failed to move item to cart.");
    }
  };

  // ⬇️ NEW function to handle checkout
  const handleCheckout = async () => {
    if (!token) {
      // Redirect to login page if not logged in
      navigate("/login");
    } else if (wishlistItems.length === 0) {
      showWarning("Your wishlist is empty. Add items to proceed to checkout.");
    } else {
      try {
        // Move all wishlist items to cart first
        const movePromises = wishlistItems.map(item => 
          sendRequest(
            "POST",
            `${BASE_URL}/v1/public_site/customers/${currentUser.data.customer.id}/cart/cart_items`,
            {
              customer_id: currentUser.data.customer.id,
              item_id: item.item_id,
              quantity: 1,
            },
            { Authorization: token }
          )
        );

        await Promise.all(movePromises);
        
        // Clear wishlist after moving all items to cart
        const removePromises = wishlistItems.map(item =>
          sendRequest(
            "DELETE",
            `${BASE_URL}/v1/public_site/wishlists/${item.item_id}`,
            null,
            { Authorization: token }
          )
        );

        await Promise.all(removePromises);
        
        // Clear wishlist state
        setWishlistItems([]);
        
        // Show success message
        showSuccess("All items moved to cart! Redirecting to checkout...");
        
        // Navigate to checkout after a short delay
        setTimeout(() => {
          navigate("/checkout");
        }, 1500);
        
      } catch (error) {
        console.error("Error moving items to cart:", error);
        showError("Failed to move items to cart. Please try again.");
      }
    }
  };

  if (!token) {
    return (
      <div className="wishlist-container">
        <h1 className="wishlist-title">Your Wishlist</h1>
        <p className="wishlist-message">Please log in to view your wishlist.</p>
        <button 
          className="wishlist-checkout-btn" 
          onClick={() => navigate("/login")}
        >
          Login to Continue
        </button>
      </div>
    );
  }

  if (loading) {
    return <p className="wishlist-message">Loading your wishlist...</p>;
  }
  

  const openLoginModal = () => {
    console.log("Opening login modal");
    setLoginModalOpen(true);
    
    if (currentUser && currentUser.data && currentUser.data.customer_data) {
      setCustomerData(currentUser.data.customer_data);
    }
  };
  
  const closeLoginModal = () => {
    setLoginModalOpen(false);
  };
  
  const handleLoginSuccess = () => {
    console.log("Login successful");
    closeLoginModal();
  };
  return (
    <div className="wishlist-container">
      <h1 className="wishlist-title">Your Wishlist</h1>

      {wishlistItems.length === 0 ? (
        <p className="wishlist-message">No items in your wishlist yet.</p>
      ) : (
        <>
          <div className="wishlist-grid">
            {wishlistItems.map((item) => (
              <div key={item.id} className="wishlist-card">
                <img
                  src={
                    item.item_image_url ||
                    "https://images.unsplash.com/photo-1592078615290-033ee584e267?auto=format&fit=crop&w=300&h=200&q=50"
                  }
                  alt="Wishlist Item"
                  className="wishlist-image"
                />
                <div className="wishlist-content">
                  <h2 className="wishlist-item-title">{item?.item_name?.en || item?.item_name || "Unnamed Item"}</h2>
                  <p className="wishlist-description">
                    Code: {item?.item_code || "N/A"}
                  </p>
                  <p className="wishlist-price">
                  {item?.sales_discount > 0 ? (
                    <>
                      <span style={{ textDecoration: 'line-through', color: '#999', marginRight: '5px' }}>
                        ₹{item?.sales_price || "N/A"}
                      </span>
                      <span>₹{(item?.sales_price - item?.sales_discount) || "N/A"}</span>
                    </>
                  ) : (
                    `₹${item?.sales_price || "N/A"}`
                  )}
                </p>
                  <div className="wishlist-buttons">
                    <button className="wishlist-move-to-cart-btn" onClick={() => handleMoveToCart(item)}>
                      Move to Cart
                    </button>
                    <button className="wishlist-remove-btn" onClick={() => handleRemove(item.item_id)}>
                      Remove from Wishlist
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          {/* Proceed to Checkout button - outside the card loop */}
          {/* <div className="wishlist-checkout-container">
            <button 
              className="wishlist-checkout-btn" 
              onClick={handleCheckout}
              disabled={wishlistItems.length === 0}
            >
              Proceed to Checkout
            </button>
          </div> */}
        </>
      )}
       <Login 
        isOpen={loginModalOpen}
        onClose={closeLoginModal}
        onLoginSuccess={handleLoginSuccess}
        customerData={customerData}
      />
      
      <MessageModal
        isOpen={modalState.isOpen}
        message={modalState.message}
        type={modalState.type}
        onClose={closeModal}
        autoClose={modalState.autoClose}
        autoCloseDelay={modalState.autoCloseDelay}
        showCloseButton={modalState.showCloseButton}
      />
    </div>
  );
};

export default WishlistPage;